import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { ASSET_TYPE_PHONE } from '../../constants/asset';
import { assetsState } from '../../storage/assets';
import { Asset } from '../../types/asset';
import { getAssetTitle } from '../../utils/assets';
import { EnhancedLabeledSelect } from '../ui/EnhancedLabeledSelect';

type Props = {
  onChange: (assetId: Asset) => void;
  assetId?: string;
  maxWidth?: number;
  disabled?: boolean;
  selectMaxWidth?: number;
  label?: string;
  maxDropdownHeight?: string;
  helpText?: React.ReactNode;
  error?: React.ReactNode;
  defaultSearch?: string;
  required?: boolean;
  placeholder?: string;
  inline?: boolean;
  showEmptyOption?: boolean;
  onClear?: () => void;
};

export function AssetPicker({
  onChange,
  assetId,
  maxWidth,
  selectMaxWidth,
  disabled = false,
  label = 'Selecciona un activo:',
  placeholder,
  maxDropdownHeight,
  helpText,
  error,
  defaultSearch,
  required,
  inline,
  showEmptyOption = false,
  onClear,
}: Readonly<Props>) {
  const assets = useRecoilValue(assetsState);

  const assetOptions = useMemo(() => {
    if (!assets) return;
    const assetOptionsList = assets.filter((asset) => asset.assetType !== ASSET_TYPE_PHONE);
    const options: { id: string | undefined; label: string; asset?: Asset }[] = assetOptionsList.map((asset) => ({
      id: asset._id,
      asset: asset,
      label: `${getAssetTitle(asset)} (${asset.label})`,
    }));
    if (showEmptyOption) {
      options.unshift({
        id: undefined,
        label: 'Todos',
      });
    }
    return options;
  }, [assets, showEmptyOption]);

  const value = assetId || showEmptyOption ? [{ id: assetId }] : [];

  return (
    <EnhancedLabeledSelect
      label={label}
      placeholder={placeholder}
      options={assetOptions}
      value={value}
      onChange={(params) => {
        if (params.option?.asset) {
          onChange(params.option.asset as Asset);
        } else {
          onClear?.();
        }
      }}
      inline={inline}
      inset={inline}
      searchable
      maxWidth={maxWidth}
      selectMaxWidth={selectMaxWidth}
      disabled={disabled}
      maxDropdownHeight={maxDropdownHeight}
      defaultSearch={defaultSearch}
      caption={helpText}
      error={error}
      required={required}
    />
  );
}
